const en: Record<string, string> = {
  // ── App ──────────────────────────────────────────────────────────────────
  'app.title': 'XTM Express',

  // ── Login ────────────────────────────────────────────────────────────────
  'login.title': 'XTM Express',
  'login.subtitle': 'Enterprise AI Translation Portal',
  'login.description': 'Sign in with your corporate credentials to start translating documents',
  'login.ssoButton': 'Sign in with SSO',
  'login.footer': 'Secured by Zitadel \u00b7 OAuth 2.1',

  // ── Header ───────────────────────────────────────────────────────────────
  'header.title': 'XTM Express',
  'header.avatarLabel': 'User menu',
  'header.languageLabel': 'Select language',
  'header.home': 'XTM Go home',
  'header.signOut': 'Sign out',
  'header.adminXtmCloud': 'Configuration',
  'header.translatedFiles': 'Translated files',
  'header.help': 'Help and resources',
  'header.help.documentation': 'Documentation',
  'header.help.apiDocumentation': 'API documentation',

  // ── Auth ─────────────────────────────────────────────────────────────────
  'auth.callback.loading': 'Signing you in…',
  'accessDenied.title': 'Access denied',
  'accessDenied.body': 'You are signed in, but your account is not granted access to XTM Go. Contact your administrator if you believe this is a mistake.',

  // ── Stepper ──────────────────────────────────────────────────────────────
  'stepper.upload': 'Upload',
  'stepper.analyze': 'Analyze',
  'stepper.translate': 'Translate',

  // ── Upload Page ──────────────────────────────────────────────────────────
  'upload.title': 'Upload your content',
  'upload.tab.file': 'Upload file',
  'upload.tab.text': 'Enter text',
  'upload.text.placeholder': 'Insert or type text for translation…',
  'upload.text.charCount': '{count} / {max} characters',
  'upload.text.tooLong': 'The text exceeds the {max} character limit. Please shorten it.',
  'upload.dropzone.title': 'Drop files here',
  'upload.dropzone.or': 'or',
  'upload.dropzone.browse': 'Browse files',
  'upload.dropzone.formats': 'Supports {formats} \u00b7 up to 5 files \u00b7 10 MB per file',
  'upload.sourceLanguage': 'Source language',
  'upload.sourceLanguage.placeholder': 'Select source language...',
  'upload.targetLanguages': 'Target languages',
  'upload.targetLanguages.placeholder': 'Search and select target languages...',
  'upload.targetLanguages.selected': '{count} languages selected',
  'languagePicker.loading': 'Loading languages…',
  'languagePicker.loadError': 'The list of languages could not be loaded.',
  'languagePicker.retry': 'Try again',
  'languagePicker.noMatch': 'No languages or variants match “{query}”.',
  'languagePicker.regionalVariants': 'Regional variants',
  'languagePicker.showMore': 'Show {count} more {language} variants',
  'languagePicker.default': 'Default',
  'languagePicker.variantCount': '{count, plural, one {# variant} other {# variants}}',
  'languagePicker.languageOption': '{language}, {count} variants, {selected} selected',
  'languagePicker.showVariants': 'Show {language} variants',
  'languagePicker.hideVariants': 'Hide {language} variants',
  'languagePicker.remove': 'Remove {name}',
  'languagePicker.changeVariant': 'Change {language} variant',
  'languagePicker.changeVariantOf': 'Change variant of {name}',
  'languagePicker.searchVariants': 'Search {count} variants…',
  'languagePicker.searchVariantsOf': 'Search {language} variants',
  'languagePicker.noVariantMatch': 'No variants match.',
  'languagePicker.allVariants': 'All variants',
  'languagePicker.added': 'added',
  'languagePicker.keys.move': 'move',
  'languagePicker.keys.toggle': 'select / deselect',
  'languagePicker.keys.expand': 'show variants',
  'languagePicker.keys.collapse': 'hide variants',
  'languagePicker.keys.clear': 'clear search',
  'upload.files.summary': '{count} files uploaded \u00b7 {size}',
  'upload.files.column.name': 'File',
  'upload.files.column.size': 'Size',
  'upload.files.column.actions': '',
  'upload.files.remove': 'Remove file',
  'upload.files.addMore': 'Add more files',
  'upload.analyzeButton': 'Analyze content',
  'upload.maxFiles': 'You can upload up to 5 files. Some files were not added.',
  'upload.error.unsupportedType': '“{name}” is not a supported format and was not added. Supported formats: {formats}.',
  'upload.error.zipTooMany': '“{name}” contains {count} files. A zip may include up to 5 files.',
  'upload.uploading': 'Uploading files...',
  'upload.error': 'Upload failed. Please try again.',
  'upload.error.tooLarge': 'The file “{name}” exceeds the {max} upload limit. Please choose a smaller file.',

  // ── Analysis Page ────────────────────────────────────────────────────────
  'analysis.title': 'AI Risk Analysis',
  'analysis.subtitle': 'Our AI engine has evaluated each file for translation complexity and regulatory risk. Check the suggested lane for each file, then accept it or choose the other lane.',
  'analysis.risk.high': 'High Risk',
  'analysis.risk.low': 'Low Risk',
  'analysis.risk.high.tooltip': 'Sensitive, complex, or business-critical content where translation errors carry real risk. Routed to a professional human reviewer.',
  'analysis.risk.low.tooltip': 'Straightforward content the AI engine can translate reliably on its own. Translated instantly and ready to download.',
  'analysis.risk.borderline': 'Near boundary',
  'analysis.risk.failed': 'Analysis failed',
  'analysis.risk.failed.tooltip': 'The AI risk analysis failed for this file, so no risk level was determined. As a fail-safe, Human in the center is suggested, where a professional linguist handles the file.',
  'analysis.risk.failed.message': 'Risk analysis failed \u2014 Human in the center is suggested as a fail-safe.',
  'analysis.failedBanner': 'Risk analysis failed for {count, plural, one {# file} other {# files}}. Ensure that your LLM connection is correct, then re-upload the file or re-enter the text.',
  'analysis.risk.failed.message.timedOut': 'Risk analysis didn\'t finish within {minutes, plural, one {# minute} other {# minutes}}, so no risk level was determined. Human in the center is suggested as a fail-safe.',
  'analysis.risk.failed.message.timedOutNoLimit': 'Risk analysis didn\'t finish in time, so no risk level was determined. Human in the center is suggested as a fail-safe.',
  'analysis.risk.failed.message.assessmentFailed': 'The risk analysis service couldn\'t assess this file, so no risk level was determined. Human in the center is suggested as a fail-safe.',
  'analysis.risk.failed.message.noLlmConnector': 'No LLM connection is set up for your organization, so this file wasn\'t assessed. Human in the center is suggested as a fail-safe.',
  'analysis.failedBanner.timedOut': 'Risk analysis didn\'t finish in time for {count, plural, one {# file} other {# files}}. Human in the center is suggested as a fail-safe; you can still choose AI-Direct.',
  'analysis.unreadable.badge': 'Couldn\'t read file',
  'analysis.unreadable.message': '<b>XTM Go couldn\'t parse this file</b>, so it was not assessed and can\'t be translated.',
  'analysis.unreadable.remove': 'Remove from batch',
  'analysis.unreadable.remove.aria': 'Remove {filename} from batch',
  'analysis.unreadable.removed': 'Removed from batch, will not be translated.',
  'analysis.unreadable.undo': 'Undo',
  'analysis.unreadable.undo.aria': 'Undo removal of {filename}',
  'analysis.unreadable.banner.single': '<b>This file couldn\'t be read, so it can\'t be translated.</b> Fix the file and start a new translation.',
  'analysis.unreadable.banner.all': '<b>None of the {count} files could be read, so nothing can be translated.</b> Fix the files and start a new translation.',
  'analysis.unreadable.banner.some': '<b>{count, plural, one {# file} other {# files}} couldn\'t be read, so this batch can\'t be translated yet.</b> Remove {count, plural, one {# file} other {# files}} from the batch to translate the other {others, plural, one {# file} other {# files}}.',
  'analysis.translateButton.removeFailed': 'Remove {count, plural, one {# failed file} other {# failed files}} to translate',
  'analysis.announce.removeFirst': 'Remove {count, plural, one {# file} other {# files}} that couldn\'t be read before translating.',
  'analysis.announce.removed': '{filename} removed from the batch. It will not be translated.',
  'analysis.announce.restored': '{filename} is back in the batch. The batch can\'t be translated while it is included.',
  'analysis.route.aiDirect': 'AI-Direct',
  'analysis.route.aiDirect.tooltip': 'Translated instantly by AI and ready to download in minutes. Recommended for low-risk content.',
  'analysis.route.xtmCloud': 'Human in the center',
  'analysis.route.xtmCloud.tooltip': 'Routed to a professional human reviewer before delivery. Recommended for high-risk or complex content.',
  'analysis.translateButton': 'Translate',
  'analysis.translateButton.files': 'Translate {count, plural, one {# file} other {# files}}',
  'analysis.translateButton.decideMore': 'Decide {count} more to translate',
  'analysis.translateButton.decideSingle': 'Accept or change the lane to translate',
  'analysis.risk.borderline.hint': '<b>Near boundary:</b> the score is close to the line between Low and High risk, so the suggestion could have gone the other way. Check this file carefully.',
  'analysis.suggestion.label': 'AI suggestion',
  'analysis.suggestion.labelFailSafe': 'Fail-safe suggestion',
  'analysis.decision.accept': 'Accept suggestion',
  'analysis.decision.accept.aria': 'Accept suggestion for {filename}',
  'analysis.decision.useOther': 'Use {lane} instead',
  'analysis.decision.useOther.aria': 'Use {lane} instead for {filename}',
  'analysis.decision.accepted': 'Accepted: <b>{lane}</b>',
  'analysis.decision.changed': 'Changed to <b>{lane}</b> (AI suggested {suggested})',
  'analysis.decision.changedFailSafe': 'Changed to <b>{lane}</b> (fail-safe suggestion: {suggested})',
  'analysis.decision.change': 'Change',
  'analysis.decision.change.aria': 'Change decision for {filename}',
  'analysis.decision.acceptAll': 'Accept all remaining suggestions',
  'analysis.styleGuide.label': 'Style guide',
  'analysis.languagePair.aria': 'Translated from {source} into {targets}',
  'analysis.languagePair.noSource.aria': 'Translated into {targets}',
  'analysis.styleGuide.choose.aria': 'Style guide for {filename}: {name}',
  'analysis.styleGuide.popup.aria': 'Choose a style guide',
  'analysis.styleGuide.default': 'Default',
  'analysis.styleGuide.none': 'No style guide',
  'analysis.styleGuide.none.description': 'Translate this file without a style guide.',
  'analysis.styleGuide.search': 'Search by name or language code',
  'analysis.styleGuide.search.aria': 'Search style guides',
  'analysis.styleGuide.noMatch': 'No style guides match “{query}”.',
  'analysis.styleGuide.updated': 'Updated {date}',
  'analysis.styleGuide.help.default': "Follows your organization's default style guide when translation starts.",
  'analysis.styleGuide.help.none': "This file is translated without a style guide. Your organization's default is <b>{name}</b>.",
  'analysis.styleGuide.help.noneNoDefault': 'This file is translated without a style guide.',
  'analysis.styleGuide.help.selected': 'Changed from the default (<b>{name}</b>). Applies to {languages}.',
  'analysis.styleGuide.help.selectedNoDefault': 'Changed from the default (no style guide). Applies to {languages}.',
  'analysis.styleGuide.help.selectedNoLanguages': 'Changed from the default (<b>{name}</b>).',
  'analysis.styleGuide.help.selectedNoDefaultNoLanguages': 'Changed from the default (no style guide).',
  'analysis.styleGuide.useDefault': 'Use default',
  'analysis.styleGuide.loadFailed': "Style guides couldn't be loaded. This file uses your organization's default.",
  'analysis.styleGuide.loading': 'Loading style guides…',
  'analysis.styleGuide.refreshFailed': "The list couldn't be refreshed. Your choice is kept.",
  'analysis.xtmCloud.customer.label': 'XTM Cloud customer',
  'analysis.xtmCloud.template.label': 'Project template',
  'analysis.xtmCloud.customer.choose.aria': 'XTM Cloud customer for {filename}: {name}',
  'analysis.xtmCloud.template.choose.aria': 'Project template for {filename}: {name}',
  'analysis.xtmCloud.customer.popup.aria': 'Choose an XTM Cloud customer',
  'analysis.xtmCloud.template.popup.aria': 'Choose a project template',
  'analysis.xtmCloud.search': 'Search by name',
  'analysis.xtmCloud.customer.search.aria': 'Search XTM Cloud customers',
  'analysis.xtmCloud.template.search.aria': 'Search project templates',
  'analysis.xtmCloud.customer.noMatch': 'No customers match “{query}”.',
  'analysis.xtmCloud.template.noMatch': 'No templates match “{query}”.',
  'analysis.xtmCloud.default': 'Default',
  'analysis.xtmCloud.customer.automatic': 'XTM Go (created automatically)',
  'analysis.xtmCloud.customer.unknown': 'Customer {id}',
  'analysis.xtmCloud.template.none': 'No template',
  'analysis.xtmCloud.template.global': 'Global',
  'analysis.xtmCloud.template.unknown': 'Template {id}',
  'analysis.xtmCloud.help.default': "Follows your organization's default customer and template when the file is sent to XTM Cloud.",
  'analysis.xtmCloud.help.selected': 'Changed from the default (<b>{customer}</b>, <b>{template}</b>).',
  'analysis.xtmCloud.help.selectedAutomatic': 'Changed from the default: <b>XTM Go</b>, created automatically; <b>{template}</b>.',
  'analysis.xtmCloud.useDefault': 'Use default',
  'analysis.xtmCloud.loading': 'Loading customers…',
  'analysis.xtmCloud.templates.loading': 'Loading templates…',
  'analysis.xtmCloud.loadFailed': "XTM Cloud customers couldn't be loaded. This file uses your organization's default customer and template.",
  'analysis.xtmCloud.templates.loadFailed': "Templates couldn't be loaded for this customer.",
  'analysis.xtmCloud.refreshFailed': "The list couldn't be refreshed. Your choice is kept.",
  'analysis.laneDecision.styleGuideGone': 'The style guide chosen for {filename} no longer exists. Choose another one.',
  'analysis.laneDecision.xtmCustomerGone': 'The XTM Cloud customer chosen for {filename} is no longer available. Choose another one.',
  'analysis.laneDecision.xtmTemplateGone': 'The project template chosen for {filename} is no longer available. Choose another one.',
  'analysis.laneDecision.xtmCloudUnavailable': "XTM Go couldn't reach XTM Cloud to check the customer and template chosen for {filename}. Try again.",
  'analysis.announce.accepted': '{filename}: accepted {lane}.',
  'analysis.announce.changed': '{filename}: changed to {lane}.',
  'analysis.announce.cleared': '{filename}: decision cleared.',
  'analysis.announce.acceptedAll': 'Accepted the AI suggestion for {count, plural, one {# file} other {# files}}. All files decided.',
  'analysis.announce.decideFirst': 'Decide the lane for {count, plural, one {# more file} other {# more files}} first.',
  'analysis.announce.decideFirstSingle': 'Accept or change the lane first.',
  'analysis.backToUpload': 'Back to Upload',
  'analysis.loading.hint': 'This usually takes less than a minute. You can keep this tab open — we’ll update it automatically.',
  'analysis.loading.status.STARTING': 'Getting started…',
  'analysis.loading.status.PENDING_UPLOAD': 'Waiting for your documents…',
  'analysis.loading.status.UPLOADING': 'Uploading your documents…',
  'analysis.loading.status.PARSING': 'Reading your documents…',
  'analysis.loading.status.READY_FOR_ANALYSIS': 'Queued for analysis…',
  'analysis.loading.status.ANALYZING': 'Assessing risk in your documents…',
  'analysis.loading.status.ANALYZED': 'Wrapping up the analysis…',
  'analysis.loading.status.FETCHING_REPORT': 'Putting the results together…',
  'analysis.polling.progress': 'Document {current} of {total}',
  'analysis.error': 'Analysis failed. Please try again.',
  'analysis.retry': 'Try again',
  'analysis.laneDecision.submitting': 'Submitting lane decisions...',
  'analysis.laneDecision.error': 'Failed to submit lane decisions. Please try again.',
  'analysis.deadline': 'Choose the lanes by {deadline}. After that, the uploaded files are no longer kept and you will need to upload them again.',
  'analysis.sourceExpired': 'The uploaded files are no longer kept, so they can no longer be translated. Start a new translation to upload them again.',
  'analysis.sourceExpired.partial': 'The uploaded files are no longer kept, so the files not yet sent can no longer be translated. The files already sent carry on.',
  'analysis.sourceExpired.showSent': 'See the files already sent',
  'analysis.startNew.confirmTitle': 'Discard this translation?',
  'analysis.startNew.confirmBody': 'Discard this translation and start a new one? None of the uploaded files will be translated.',
  'analysis.startNew.confirmButton': 'Discard & start new',
  'analysis.startNew.cancel': 'Cancel',
  'analysis.startNew.error': 'Couldn’t start a new translation. Please try again.',
  'analysis.noData': 'No documents to analyze. Please go back and upload files.',
  'analysis.riskReport.button': 'Risk report',
  'analysis.riskReport.veto': 'Veto triggered',
  'analysis.riskReport.failed': 'Factor failed',
  'analysis.riskReport.factor.criticalDownstreamImpactRisk': 'Critical downstream impact',
  'analysis.riskReport.factor.structuralFormatIntegrityRisk': 'Structural / format integrity',
  'analysis.riskReport.factor.domainDifficultyClassification': 'Domain difficulty',
  'analysis.riskReport.factor.referencelessTranslationDifficultyEstimation': 'Translation difficulty',

  // ── Translate Page ───────────────────────────────────────────────────────
  'translate.progress.title': 'Translating your content...',
  'translate.progress.info': 'Processing {current} of {total} documents',
  'translate.progress.hint': 'This usually takes a few minutes. You can keep this tab open — we’ll update it automatically.',
  'translate.progress.stage.prepare': 'Preparing your content for the AI engine…',
  'translate.progress.stage.context': 'Reading the context of each segment…',
  'translate.progress.stage.translate': 'Translating segment by segment…',
  'translate.progress.stage.terminology': 'Keeping terminology and tone consistent…',
  'translate.progress.stage.quality': 'Checking formatting and structure…',
  'translate.complete.title': 'Processing complete',
  'translate.complete.filesReady': '{count, plural, one {# file is} other {# files are}} ready for download.',
  'translate.complete.inReview': '{count, plural, one {# document was} other {# documents were}} sent to TMS for the Human in the center flow. You will get an email when the translated files are ready for download.',
  'translate.complete.expandAll': 'Expand all',
  'translate.complete.collapseAll': 'Collapse all',
  'translate.complete.showLanguages': 'Show translated files for {document}',
  'translate.complete.hideLanguages': 'Hide translated files for {document}',
  'translate.complete.fileCount': '{count, plural, one {# file} other {# files}}',
  'translate.complete.readyOf': '{ready} of {total} ready',
  'translate.complete.languagesNotDelivered': '{count, plural, one {# language not delivered.} other {# languages not delivered.}}',
  'translate.complete.languagesWithWarning': '{count, plural, one {# language delivered with a warning.} other {# languages delivered with a warning.}}',
  'translate.partial.banner.title': 'Some documents need attention',
  'translate.partial.banner.description': 'Some jobs failed. You can retry the failed ones below.',
  'translate.table.column.file': 'Document',
  'translate.table.column.language': 'Languages',
  'translate.table.column.route': 'Lane',
  'translate.table.column.status': 'Status',
  'translate.table.column.action': 'Action',
  'translate.status.PENDING': 'Pending',
  'translate.status.QUEUED': 'Queued',
  'translate.status.PROCESSING': 'Processing',
  'translate.status.GENERATING_TARGETS': 'Generating translations',
  'translate.status.TRANSLATING': 'Translating',
  'translate.status.DELIVERING': 'Delivering',
  'translate.status.IN_PROGRESS': 'In Progress',
  'translate.status.COMPLETED': 'Completed',
  'translate.status.FAILED': 'Failed',
  'translate.status.inReview': 'In human review',
  'translate.status.sent': 'Sent',
  'translate.action.download': 'Download',
  'translate.action.viewInXtm': 'View review',
  'translate.action.retry': 'Retry',
  'translate.action.downloading': 'Downloading...',
  'translate.action.retrying': 'Retrying...',
  'translate.action.retried': 'Retried',
  'translate.action.downloadAll': 'Download all',
  'translate.action.downloadLanguage': 'Download {document}, {language}',
  'translate.action.downloaded': 'Downloaded',
  'translate.newTranslation': 'Start a new translation',
  'translate.downloadAll': 'Download All as ZIP',
  'translate.downloadCompleted': 'Download completed',
  'translate.loading': 'Preparing translation jobs...',
  'translate.error': 'Translation failed. Please try again.',
  'translate.noJobs': 'No translation jobs found. Please go back and submit lane decisions.',
  'translate.failureReason': 'Reason: {reason}',
  'translate.retry.error': 'Retry failed. Try again.',
  'translate.retry.sourceExpired': 'The uploaded file is no longer kept, so it cannot be retried. Upload it again in a new translation.',

  // ── Languages ────────────────────────────────────────────────────────────
  'language.en': 'English',
  'language.fr': 'French',
  'language.de': 'German',
  'language.es': 'Spanish',
  'language.ja': 'Japanese',
  'language.zh': 'Chinese (Simplified)',
  'language.pt': 'Portuguese',
  'language.it': 'Italian',
  'language.ko': 'Korean',
  'language.ar': 'Arabic',
  'language.nl': 'Dutch',
  'language.pl': 'Polish',
  'language.sv': 'Swedish',
  'language.el': 'Greek',

  // ── Shared ───────────────────────────────────────────────────────────────
  'shared.loading': 'Loading...',
  'shared.error.title': 'Something went wrong',
  'shared.error.description': 'An unexpected error occurred. Please try refreshing the page.',
  'shared.error.retry': 'Try Again',
  'shared.empty': 'No data available',

  // ── Admin: XTM Cloud ─────────────────────────────────────────────────────
  'admin.xtmCloud.title': 'Configuration',
  'admin.xtmCloud.subtitle': 'Manage your XTM Cloud integration and AI translation settings.',
  'admin.xtmCloud.status.done': 'Configured',
  'admin.xtmCloud.status.pending': 'Pending',
  'admin.xtmCloud.overview.title': 'Setup overview',
  'admin.xtmCloud.nav.label': 'Configuration sections',
  'admin.xtmCloud.nav.unsaved': 'has unsaved changes',
  'admin.xtmCloud.customer.problem.authentication': 'XTM Cloud rejected the saved credentials, so the list could not load. Fix them above and save.',
  'admin.xtmCloud.customer.problem.unavailable': 'XTM Cloud did not respond, so the list could not load. The saved credentials are fine.',
  'admin.xtmCloud.nav.problem': 'XTM Cloud connection is not working',
  'admin.xtmCloud.retry': 'Retry',
  'admin.xtmCloud.connector.title': 'LLM connector',
  'admin.xtmCloud.footer.unsaved': 'Unsaved changes',
  'admin.xtmCloud.footer.save': 'Save changes',
  'admin.xtmCloud.footer.discard': 'Discard',
  'admin.xtmCloud.loading': 'Loading configuration…',
  'admin.xtmCloud.loadError': 'Could not load the XTM Cloud configuration.',
  'admin.xtmCloud.connection.title': 'XTM Cloud connection',
  'admin.xtmCloud.connection.description': 'Connect XTM Go to your organization\'s XTM Cloud account and choose the default customer and project template for files sent to Human in the center. The base URL comes from your organization\'s SSO settings and can\'t be changed here.',
  'admin.xtmCloud.baseUrl': 'Base URL',
  'admin.xtmCloud.client': 'Client',
  'admin.xtmCloud.username': 'Username',
  'admin.xtmCloud.password': 'Password',
  'admin.xtmCloud.passwordSet': 'Password set',
  'admin.xtmCloud.passwordNotSet': 'No password yet',
  'admin.xtmCloud.saved': 'Changes saved.',
  'admin.xtmCloud.credentialsError': 'Could not save credentials.',
  'admin.xtmCloud.credentialsIncomplete': 'Client, username and password are all required to save credentials.',
  'admin.xtmCloud.customer.locked': 'Save credentials first to load customers.',
  'admin.xtmCloud.customer.label': 'Default customer',
  'admin.xtmCloud.customer.select': 'Select a customer',
  'admin.xtmCloud.customer.loading': 'Loading customers…',
  'admin.xtmCloud.customer.unavailable': 'This customer is no longer active in XTM Cloud. Choose another one.',
  'admin.xtmCloud.customerError': 'Could not save the selected customer.',
  'admin.xtmCloud.template.select': 'Default project template',
  'admin.xtmCloud.template.none': 'No template',
  'admin.xtmCloud.template.global': 'Global',
  'admin.xtmCloud.template.locked': 'Select a customer first to choose its project template.',
  'admin.xtmCloud.template.loadFailed': 'Could not load the project templates.',
  'admin.xtmCloud.template.unavailable': 'This template no longer exists in XTM Cloud. Choose another one.',
  'admin.xtmCloud.templateError': 'Could not save the project template.',
  'admin.xtmCloud.problem.authentication.title': 'XTM Cloud rejected the saved credentials',
  'admin.xtmCloud.problem.authentication.body': 'Check the client and user name below, then type the password again and save. XTM Cloud never gives a stored password back, so it cannot be checked — only replaced.',
  'admin.xtmCloud.problem.unavailable.title': 'XTM Cloud is not responding',
  'admin.xtmCloud.problem.unavailable.body': 'This is temporary and the saved credentials are fine. Try again in a moment — there is no need to change the password.',
  'admin.xtmCloud.problem.reference': 'Reference: {id}',
  'admin.xtmCloud.none': '—',
  'admin.xtmCloud.llm.description': 'Choose the AI connector used for translation.',
  'admin.xtmCloud.llm.riskAssessmentOverride': 'This connector is also used for AI risk assessment, overriding the default connector configured there.',
  'admin.xtmCloud.tools.riskAssessment': 'Risk Assessment',
  'admin.xtmCloud.tools.mtLlmGateway': 'XTM Gateway',
  'admin.xtmCloud.tools.newTab': '(opens in a new tab)',
  'admin.xtmCloud.llm.select': 'Select a connector',
  'admin.xtmCloud.llm.saved': 'Settings saved.',
  'admin.xtmCloud.llm.error': 'Could not save settings.',
  'admin.xtmCloud.llm.loadError': 'Could not load connectors.',
  'admin.xtmCloud.llm.loading': 'Loading connectors…',
  'admin.xtmCloud.llm.unavailable': 'Unavailable connector',
  'admin.xtmCloud.llm.missing': 'The saved connector is no longer available. It may have been disabled or deleted. Choose another connector.',
  'admin.xtmCloud.llm.nav.problem': 'The saved LLM connector is no longer available',
  'admin.xtmCloud.llm.translationMode': 'Translation mode',
  'admin.xtmCloud.llm.mode.single': 'Single',
  'admin.xtmCloud.llm.mode.batch': 'Batch',
  'admin.xtmCloud.llm.batchSize': 'Batch size',
  'admin.xtmCloud.llm.batchSize.hint': '1–50 documents per batch',

  // ── Translated batches ───────────────────────────────────────────────────
  'translatedBatches.title': 'Translated files',
  'translatedBatches.searchPlaceholder': 'Search by file name…',
  'translatedBatches.loading': 'Loading batches…',
  'translatedBatches.error': 'Could not load translated batches.',
  'translatedBatches.empty': 'No batches found.',
  'translatedBatches.downloadAll': 'Download all',
  'translatedBatches.openInXtm': 'Open in XTM Cloud',
  'translatedBatches.filesLoading': 'Loading files…',
  'translatedBatches.filesError': 'Could not load files for this batch.',
  'translatedBatches.noFiles': 'No translated files in this batch.',
  'translatedBatches.prev': 'Previous',
  'translatedBatches.next': 'Next',
  'translatedBatches.pageOf': 'Page {current} of {total}',
  'translatedBatches.count': '{count, plural, one {# batch} other {# batches}}',
  'translatedBatches.fileCount': '{count, plural, one {# file} other {# files}}',
  'translatedBatches.fileMeta': '{language} · from {source}',
  'translatedBatches.moreLanguages': '+{count}',
  'translatedBatches.expand': 'Show translated files',
  'translatedBatches.collapse': 'Hide translated files',
  'translatedBatches.download': 'Download',
  'translatedBatches.downloading': 'Downloading…',
  'translatedBatches.downloadFile': 'Download {filename}',
  'translatedBatches.zipping': 'Preparing ZIP…',
  'translatedBatches.status.inProgress': 'In progress',
  'translatedBatches.status.failed': 'Failed',
  'translatedBatches.status.expired': 'Expired',
  'translatedBatches.moreFiles': '+{count}',
  'translatedBatches.readyOf': '{ready} of {total} ready',
  'translatedBatches.state.translating': 'Translating',
  'translatedBatches.state.inXtmCloud': 'With translator',
  'translatedBatches.state.inXtmCloud.tooltip': 'A translator in XTM Cloud is working on this. It can take days — you will be emailed when it is ready.',
  'translatedBatches.state.unavailable': 'In XTM Cloud only',
  'translatedBatches.state.unavailable.tooltip': 'The translation finished, but the file could not be brought back into XTM Go. Open the XTM Cloud project to collect it.',
  'translatedBatches.state.failed': 'Failed',
  'translatedBatches.noResults': 'Nothing matches “{query}”.',
  'translatedBatches.noResultsHint': 'Try a different file name.',
  'translatedBatches.emptyHint': 'Files you translate will show up here.',
  'translatedBatches.clearSearch': 'Clear search',
  'translatedBatches.pagination': 'Pagination',
  'translatedBatches.single.title': 'Translated batch',
  'translatedBatches.single.backToAll': 'All translated files',
  'translatedBatches.single.notFound': 'This batch does not exist or you have no privileges to read it.',
  'translatedBatches.single.notFoundHint': 'Check the link, or open all your translated files.',
  'translatedBatches.single.error': 'Could not load this batch.',
  'admin.styleGuides.title': 'Style guides',
  'admin.styleGuides.nav.problem': 'The default style guide no longer exists',
  'admin.styleGuides.description': 'XTM Go shares style guides with XTM Cloud and other XTM products, so any edits or deletions made here will apply everywhere. However, the default you select below only applies to the AI-Direct lane in XTM Go.',
  'admin.styleGuides.summary.using': 'AI-Direct translations use {name} by default.',
  'admin.styleGuides.summary.none': 'No default style guide. AI-Direct translations run without one.',
  'admin.styleGuides.create': 'New style guide',
  'admin.styleGuides.edit': 'Edit {name}',
  'admin.styleGuides.delete': 'Delete {name}',
  'admin.styleGuides.action.edit': 'Edit',
  'admin.styleGuides.action.delete': 'Delete',
  'admin.styleGuides.action.useNoDefault': 'Use no style guide',
  'admin.styleGuides.delete.title': 'Delete {name}?',
  'admin.styleGuides.delete.body': 'This permanently deletes {name} for all users, everywhere it\'s used, including XTM Cloud.',
  'admin.styleGuides.delete.bodySelected': 'This permanently deletes {name} for all users, everywhere it\'s used, including XTM Cloud. Because it\'s the default style guide, AI-Direct translations will fail until you choose another default or No style guide.',
  'admin.styleGuides.delete.confirm': 'Delete',
  'admin.styleGuides.delete.cancel': 'Cancel',
  'admin.styleGuides.delete.error': 'Could not delete {name}. Nothing was changed.',
  'admin.styleGuides.delete.busy': 'Could not delete {name} right now. Try again in a moment.',
  'admin.styleGuides.loadError': 'We could not read your style guides. This is a problem reading them, not a sign that you have none — your default style guide is unchanged.',
  'admin.styleGuides.retry': 'Try again',
  'admin.styleGuides.loadFailedUseNone.hint': 'You can remove the default style guide while it can\'t be loaded, so AI-Direct translations work again.',
  'admin.styleGuides.loadFailedUseNone.done': 'No default style guide now. AI-Direct translations run without one.',
  'admin.styleGuides.noLanguages': 'No language sections',
  'admin.styleGuides.saveError': 'Could not save. {previous} is still the default.',
  'admin.styleGuides.saveErrorUnchanged': 'Could not save. Your default style guide is unchanged.',
  'admin.styleGuides.noDefault': 'No style guide',
  'admin.styleGuides.noDefault.description': 'AI-Direct translations run without a style guide.',
  'admin.styleGuides.notUsedByXtmGo': 'not used by XTM Go',
  'admin.styleGuides.warning.missing': '{name} was the default but no longer exists (noticed on {date}). AI-Direct translations fail until you choose another default or No style guide.',
  'admin.styleGuides.column.name': 'Name',
  'admin.styleGuides.column.languages': 'Languages',
  'admin.styleGuides.column.updated': 'Updated',
  'admin.styleGuides.column.default': 'Default',
  'admin.styleGuides.column.actions': 'Actions',
  'admin.styleGuides.default.checked': 'Default',
  'admin.styleGuides.default.unchecked': 'Set as default',
  'admin.styleGuides.default.noneSr': 'no style guide',
  'admin.styleGuides.editor.loading': 'Opening the style guide editor…',
  'admin.styleGuides.editor.loadError': 'The style guide editor could not be loaded. Your style guides are unchanged.',
  'admin.styleGuides.editor.back': 'Back to style guides',
  'admin.styleGuides.editor.authOrganization': 'Your organization’s identity for style guides is not configured. Set the XTM Cloud client in the XTM Cloud connection settings.',
  'admin.styleGuides.editor.authForbidden': 'You no longer have permission to edit style guides.',
  'admin.styleGuides.editor.authFailed': 'Your editing session ended. Reopen the editor to carry on; unsaved changes are kept until you leave.',
  'admin.styleGuides.editor.unavailable': 'This style guide is not available.',
  'admin.styleGuides.editor.navigationNote': 'Unsaved changes are lost if you leave by switching section or going back. Use Cancel or Save in the editor.',

  // ── Language issues (COPA-877, COPA-878) ─────────────────────────────────
  // Why a target language was not delivered, or was delivered with a warning. The English is the spec's text verbatim.
  // XTM_CLOUD_PROJECT_MAY_EXIST doubles its quotes: in ICU an apostrophe before a brace starts a quoted literal.
  'languageIssue.FILE_NOT_CREATED': '{language}: the translated file couldn\'t be created.',
  'languageIssue.FILE_NOT_CREATED_IN_TIME': '{language}: the translated file couldn\'t be created in time.',
  'languageIssue.TOO_MUCH_UNTRANSLATED': '{language}: too much of the text couldn\'t be translated.',
  'languageIssue.CONNECTOR_STALLED': '{language}: your LLM connector stopped responding on the AI Direct lane.',
  'languageIssue.CONNECTOR_UNAVAILABLE': '{language}: the AI Direct lane couldn\'t reach your LLM connector.',
  'languageIssue.STYLE_GUIDE_UNAVAILABLE': '{language}: Your organization\'s style guide couldn\'t be loaded. Try again, or send the file to XTM Cloud.',
  'languageIssue.STYLE_GUIDE_DELETED': '{language}: Your organization\'s style guide was deleted. Ask an administrator to choose another option, or send the file to XTM Cloud.',
  'languageIssue.STYLE_GUIDE_NOT_CONFIGURED': '{language}: XTM Go couldn\'t reach your organization\'s style guide because its connection isn\'t set up. Ask an administrator to check the XTM Cloud connection, or send the file to XTM Cloud.',
  'languageIssue.STYLE_GUIDE_FILE_UNAVAILABLE': '{language}: The style guide chosen for this file couldn\'t be loaded. Try again, or send the file to XTM Cloud.',
  'languageIssue.STYLE_GUIDE_FILE_DELETED': '{language}: The style guide chosen for this file was deleted. Upload the file again, or send it to XTM Cloud.',
  'languageIssue.DELIVERED_WITH_UNTRANSLATED': '{language}: delivered with {untranslatedSegments, plural, one {# segment} other {# segments}} left in the source language because your LLM connector stopped responding.',
  'languageIssue.STOPPED_UNEXPECTEDLY': '{language}: the translation stopped unexpectedly. Try again.',
  'languageIssue.CONNECTOR_NOT_CONFIGURED': '{language}: no LLM connector is set up for your organization. Ask an administrator to set one up, or send the file to XTM Cloud.',
  'languageIssue.UNSUPPORTED_STRUCTURE': '{language}: this file\'s structure isn\'t supported. Send the file to XTM Cloud.',
  'languageIssue.SOURCE_FILE_MISSING': '{language}: a file needed for the translation is no longer available. Upload the file again.',
  'languageIssue.PARSED_DOCUMENT_UNREADABLE': '{language}: the translated file couldn\'t be created. Upload the file again.',
  'languageIssue.UPLOADED_BEFORE_UPDATE': '{language}: upload the file again; it was uploaded before an update.',
  'languageIssue.SOURCE_LANGUAGE_NO_CONNECTOR': '{language}: the source language couldn\'t be detected because no LLM connector is set up. Ask an administrator to set one up.',
  'languageIssue.SOURCE_LANGUAGE_CONNECTOR_FAILED': '{language}: the source language couldn\'t be detected because your LLM connector didn\'t answer. Try again.',
  'languageIssue.XTM_CLOUD_CONNECTION': '{language}: XTM Go couldn\'t connect to XTM Cloud. Ask an administrator to check the XTM Cloud connection.',
  'languageIssue.XTM_CLOUD_UNSUPPORTED_FORMAT': '{language}: XTM Cloud doesn\'t support this file format.',
  'languageIssue.XTM_CLOUD_PASSWORD_PROTECTED': '{language}: XTM Cloud refused the file; it may be password-protected.',
  'languageIssue.XTM_CLOUD_FILE_TOO_LARGE': '{language}: the file is larger than XTM Cloud accepts (limit: {limit}).',
  'languageIssue.XTM_CLOUD_FILE_TOO_LARGE_NO_LIMIT': '{language}: the file is larger than XTM Cloud accepts.',
  'languageIssue.XTM_CLOUD_PROJECT_REFUSED': '{language}: XTM Cloud refused the project. Try again.',
  'languageIssue.XTM_CLOUD_PROJECT_MAY_EXIST': '{language}: an XTM Cloud project may already exist (\'\'{projectName}\'\'). Check XTM Cloud before trying again.',
  'languageIssue.XTM_CLOUD_HANDOVER_FAILED': '{language}: the file couldn\'t be sent to XTM Cloud. Try again.',
  'languageIssue.XTM_CLOUD_TARGET_NOT_RETRIEVED': '{language}: the translated file couldn\'t be retrieved from XTM Cloud.',
  'languageIssue.XTM_CLOUD_CUSTOMER_UNAVAILABLE': '{language}: The XTM Cloud customer chosen for this file is no longer available. Decide the file again with another customer, or send it to AI-Direct.',
  'languageIssue.XTM_CLOUD_TEMPLATE_UNAVAILABLE': '{language}: The project template chosen for this file is no longer available in XTM Cloud. Decide the file again with another template, or send it to AI-Direct.',
  'languageIssue.XTM_CLOUD_DEFAULT_CUSTOMER_UNAVAILABLE': '{language}: Your organization\'s XTM Cloud customer is no longer available. Ask an administrator to choose another customer, or send the file to AI-Direct.',
  'languageIssue.XTM_CLOUD_DEFAULT_TEMPLATE_UNAVAILABLE': '{language}: Your organization\'s project template is no longer available in XTM Cloud. Ask an administrator to choose another template, or send the file to AI-Direct.',
  'languageIssue.UNKNOWN_FAILURE': '{language}: the translation stopped unexpectedly. Try again.',
  'languageIssue.UNKNOWN_WARNING': '{language}: delivered with a warning.',
  'languageIssue.reference': 'Reference: {uploadBatchId}',
  'languageIssue.copyReference': 'Copy reference',
};

export default en;
